import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Users,
  Activity,
  Calendar,
  Filter,
  Download,
  RefreshCw,
  AlertCircle,
  Target,
  Zap
} from 'lucide-react';
import { WorkflowService } from '../services/WorkflowService';

const WorkflowAnalytics = ({ userProfile }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('30days');
  const [selectedTenant, setSelectedTenant] = useState('all');
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    loadAnalytics();
  }, [timeRange, selectedTenant]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      
      // Get analytics data
      const data = await WorkflowService.getWorkflowAnalytics(
        selectedTenant === 'all' ? null : selectedTenant
      );
      
      // Calculate additional metrics
      const completionRate = data.total > 0 
        ? ((data.byStatus.completed || 0) / data.total * 100).toFixed(1)
        : 0;
      
      const approvalRate = data.total > 0
        ? (((data.byStatus.approved || 0) + (data.byStatus.completed || 0)) / data.total * 100).toFixed(1)
        : 0;
      
      const avgProcessingTime = calculateAvgProcessingTime(data);
      
      setAnalytics({
        ...data,
        completionRate,
        approvalRate,
        avgProcessingTime
      });
      
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateAvgProcessingTime = (data) => {
    // This would calculate based on actual workflow data
    // For now, return a placeholder
    return '2.5 days';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#eab308',
      approved: '#22c55e',
      rejected: '#ef4444',
      completed: '#3b82f6',
      cancelled: '#6b7280',
      pending_info: '#a855f7'
    };
    return colors[status] || '#6b7280';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-blue-600" />
            Workflow Analytics
          </h2>
          <p className="text-gray-600 mt-1">
            Monitor workflow performance and metrics across your platform
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
            <option value="all">All Time</option>
          </select>
          
          <button
            onClick={loadAnalytics}
            className="p-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw size={16} />
          </button>
          
          <button className="p-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50">
            <Download size={16} />
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Workflows</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {analytics?.total || 0}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                +{analytics?.last30Days || 0} this month
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Activity className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completion Rate</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {analytics?.completionRate || 0}%
              </p>
              <div className="flex items-center gap-1 mt-1">
                <TrendingUp className="h-3 w-3 text-green-600" />
                <p className="text-xs text-green-600">+5.2% vs last period</p>
              </div>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Approval Rate</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {analytics?.approvalRate || 0}%
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Approved & Completed
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Target className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Processing</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {analytics?.avgProcessingTime || '0'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Per workflow
              </p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Status Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold mb-4">Workflow Status Distribution</h3>
          
          <div className="space-y-4">
            {analytics?.byStatus && Object.entries(analytics.byStatus).map(([status, count]) => {
              const percentage = analytics.total > 0 ? (count / analytics.total * 100).toFixed(1) : 0;
              return (
                <div key={status}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium capitalize">{status}</span>
                    <span className="text-sm text-gray-600">{count} ({percentage}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all duration-500"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: getStatusColor(status)
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold mb-4">Workflows by Template</h3>
          
          <div className="space-y-3">
            {analytics?.byTemplate && Object.entries(analytics.byTemplate).slice(0, 5).map(([templateId, count]) => (
              <div key={templateId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded">
                    <Zap className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">
                      {templateId.replace('template_', 'Template ')}
                    </p>
                    <p className="text-xs text-gray-500">
                      {((count / analytics.total) * 100).toFixed(1)}% of total
                    </p>
                  </div>
                </div>
                <span className="text-lg font-semibold">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Performance Trends */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Performance Trends</h3>
          <div className="flex items-center gap-2">
            <button className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded">Daily</button>
            <button className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded">Weekly</button>
            <button className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded">Monthly</button>
          </div>
        </div>
        
        {/* Placeholder for chart */}
        <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
          <div className="text-center">
            <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">Chart visualization would go here</p>
            <p className="text-xs text-gray-400 mt-1">Consider integrating Chart.js or Recharts</p>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4">Recent Workflow Activity</h3>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm font-medium">Special Project Request approved</p>
                <p className="text-xs text-gray-500">by John Doe • 2 minutes ago</p>
              </div>
            </div>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Approved</span>
          </div>
          
          <div className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="text-sm font-medium">Purchase Order submitted</p>
                <p className="text-xs text-gray-500">by Jane Smith • 15 minutes ago</p>
              </div>
            </div>
            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">Pending</span>
          </div>
          
          <div className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <XCircle className="h-5 w-5 text-red-600" />
              <div>
                <p className="text-sm font-medium">Budget Request rejected</p>
                <p className="text-xs text-gray-500">by Finance Team • 1 hour ago</p>
              </div>
            </div>
            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Rejected</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowAnalytics;